import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { Check, X, LogOut } from 'lucide-react'
import { format } from 'date-fns'

const SUPER_ADMIN_EMAIL = 'drewbacca15@gmail.com'

// Gets the current session's access token for authenticated API calls.
const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || null
}

const apiCall = async (method, body) => {
  const token = await getToken()
  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }
  if (body) opts.body = JSON.stringify(body)
  const res = await fetch('/api/admin/platform-admin', opts)
  if (!res.ok) {
    const d = await res.json().catch(() => ({}))
    throw new Error(d.error || 'Request failed')
  }
  return res.json()
}

export default function SuperAdminPage() {
  const { user } = useAuth()
  const [pending, setPending] = useState([])
  const [approved, setApproved] = useState([])
  const [loading, setLoading] = useState(true)
  const [actioning, setActioning] = useState(null)
  const [error, setError] = useState('')

  // Client-side guard: only render the UI for the super admin email.
  // Real authorization is enforced server-side in /api/admin/platform-admin.
  const isAuthorized = user?.email === SUPER_ADMIN_EMAIL

  useEffect(() => {
    if (!isAuthorized) return
    loadAdmins()
  }, [isAuthorized])

  const loadAdmins = async () => {
    setLoading(true)
    setError('')
    try {
      const { admins } = await apiCall('GET')
      const rows = admins || []
      setPending(rows.filter(r => r.platform_approved === false))
      setApproved(rows.filter(r => r.platform_approved !== false))
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  const approve = async (id) => {
    setActioning(id)
    setError('')
    try {
      await apiCall('POST', { action: 'approve', userId: id })
      await loadAdmins()
    } catch (err) {
      setError(err.message)
    }
    setActioning(null)
  }

  const reject = async (id) => {
    if (!window.confirm('Permanently delete this admin account?')) return
    setActioning(id)
    setError('')
    try {
      await apiCall('POST', { action: 'delete', userId: id })
      await loadAdmins()
    } catch (err) {
      setError(err.message)
    }
    setActioning(null)
  }

  if (!user) return (
    <div style={styles.screen}>
      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem' }}>Please sign in first.</p>
    </div>
  )

  if (!isAuthorized) return (
    <div style={styles.screen}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: '1rem' }}>🔒</div>
        <h2 style={{ color: 'white', fontFamily: 'Playfair Display, serif', marginBottom: '0.5rem' }}>Access Denied</h2>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem' }}>This page is restricted.</p>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: '#0B1E3D', padding: '2rem' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <div style={{ width: 32, height: 32, background: '#E8614A', borderRadius: '50% 50% 50% 0', display: 'flex', alignItems: 'center', justifyContent: 'center', transform: 'rotate(-45deg)' }}>
                <span style={{ transform: 'rotate(45deg)', display: 'block', fontSize: 16 }}>🌊</span>
              </div>
              <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', color: 'white', margin: 0 }}>
                WaveSync Platform
              </h1>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', margin: 0 }}>Super Admin · {user.email}</p>
          </div>
          <button
            onClick={() => supabase.auth.signOut().then(() => window.location.href = '/')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'DM Sans, sans-serif' }}
          >
            <LogOut size={13} /> Sign Out
          </button>
        </div>

        {error && (
          <div style={{ padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem', background: 'rgba(232,97,74,0.1)', border: '1px solid rgba(232,97,74,0.25)', color: '#f0876e', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        {/* Pending approvals */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
            <h2 style={{ color: 'white', fontSize: '1rem', fontWeight: 700, margin: 0 }}>Pending Approval</h2>
            {pending.length > 0 && (
              <span style={{ background: '#E8614A', color: 'white', borderRadius: 20, padding: '2px 9px', fontSize: '0.72rem', fontWeight: 700 }}>
                {pending.length}
              </span>
            )}
          </div>

          {loading ? (
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.875rem', padding: '1.5rem 0' }}>Loading...</div>
          ) : pending.length === 0 ? (
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '1.5rem', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.875rem' }}>
              No pending approvals 🎉
            </div>
          ) : pending.map(admin => (
            <div key={admin.id} style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(232,97,74,0.25)',
              borderLeft: '3px solid #E8614A', borderRadius: 12, padding: '1rem 1.25rem',
              marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '1rem'
            }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(232,97,74,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, color: '#E8614A', flexShrink: 0 }}>
                {(admin.full_name || 'A').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: 'white', fontSize: '0.9rem' }}>{admin.full_name || '—'}</div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', marginTop: 2 }}>
                  {admin.email} · Signed up {admin.created_at ? format(new Date(admin.created_at), 'MMM d, yyyy') : '—'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  onClick={() => approve(admin.id)}
                  disabled={actioning === admin.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(14,170,164,0.15)', border: '1px solid rgba(14,170,164,0.4)', color: '#0EAAA4', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', opacity: actioning === admin.id ? 0.5 : 1 }}
                >
                  <Check size={13} /> Approve
                </button>
                <button
                  onClick={() => reject(admin.id)}
                  disabled={actioning === admin.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(232,97,74,0.12)', border: '1px solid rgba(232,97,74,0.3)', color: '#E8614A', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', opacity: actioning === admin.id ? 0.5 : 1 }}
                >
                  <X size={13} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Approved admins */}
        <div>
          <h2 style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.875rem' }}>
            Approved Admins ({approved.length})
          </h2>
          {approved.map(admin => (
            <div key={admin.id} style={{
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 10, padding: '0.875rem 1.25rem', marginBottom: '0.5rem',
              display: 'flex', alignItems: 'center', gap: '1rem'
            }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(14,170,164,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#0EAAA4', flexShrink: 0 }}>
                {(admin.full_name || 'A').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem' }}>{admin.full_name || '—'}</div>
                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', marginTop: 1 }}>{admin.email}</div>
              </div>
              <span style={{ fontSize: '0.7rem', color: '#0EAAA4', fontWeight: 600 }}>✓ Approved</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const styles = {
  screen: {
    minHeight: '100vh', background: '#0B1E3D',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem'
  }
}
